import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Bell, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/companies", label: "Rate Employers", testid: "nav-employers", match: ["/companies", "/company"] },
  { to: "/recruiters", label: "Rate Recruiters", testid: "nav-recruiters", match: ["/recruiters", "/recruiter"] },
  { to: "/jobs", label: "Find Jobs", testid: "nav-jobs", match: ["/jobs", "/job", "/careers"] },
  { to: "/rankings", label: "Rankings", testid: "nav-rankings", match: ["/rankings"] },
  { to: "/portfolio", label: "Portfolio", testid: "nav-portfolio", match: ["/portfolio"] },
  { to: "/coverage", label: "Coverage", testid: "nav-coverage", match: ["/coverage"] },
  { to: "/p/about", label: "About", testid: "nav-about", match: ["/p/about", "/about"] },
];

export function Navbar() {
  const { user, notifications, logout, markRead } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const unread = notifications.filter((n) => !n.read).length;
  const isActive = (l) => l.match.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  return (
    <header className="sticky top-0 z-50 glass">
      <nav className="mx-auto flex h-16 max-w-[1800px] items-center justify-between px-6 sm:px-10 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <Logo size={30} />
        </Link>

        <div className="hidden items-center gap-5 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              aria-current={isActive(l) ? "page" : undefined}
              className={`text-sm transition-colors hover:text-teal-400 ${isActive(l) ? "font-bold text-teal-400" : "font-medium text-slate-300"}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notif-bell" className="relative rounded-full p-2 text-slate-300 hover:bg-black/[0.04] hover:text-teal-400" aria-label="Notifications">
                  <Bell className="h-5 w-5" aria-hidden="true" />
                  {unread > 0 && (
                    <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-teal-400" aria-hidden="true" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 border-[#DDDAD1] bg-ink-900">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-black/[0.05]" />
                {notifications.length === 0 && (
                  <div className="px-2 py-6 text-center text-sm text-slate-400">No notifications yet</div>
                )}
                {notifications.slice(0, 8).map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    data-testid="notif-item"
                    onClick={() => { markRead(n.id); if (n.link) nav(n.link); }}
                    className={`flex flex-col items-start gap-0.5 ${!n.read ? "bg-teal-500/5" : ""}`}
                  >
                    <span className="text-sm">{n.message}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full border border-[#DDDAD1] px-3 py-1.5 text-sm hover:border-teal-400/40">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-teal-500/20 text-xs text-teal-300">
                    {(user.name || "U").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 border-[#DDDAD1] bg-ink-900">
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}>My Dashboard</DropdownMenuItem>
                {(user.role === "admin" || user.role === "moderator") && (
                  <DropdownMenuItem data-testid="menu-moderation" onClick={() => nav("/moderation")}>Moderation Queue</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-black/[0.05]" />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Link to="/login" data-testid="nav-login" className="text-sm text-slate-300 hover:text-teal-400">Log in</Link>
              <Link to="/companies" data-testid="nav-cta" className="btn-teal text-sm">Write a Review</Link>
            </div>
          )}

          <button className="rounded-full p-2 text-slate-300 hover:bg-black/[0.04] lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-[#DDDAD1] px-5 py-4 lg:hidden">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`block py-2 ${isActive(l) ? "font-bold text-teal-400" : "text-slate-200"}`}>{l.label}</Link>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-slate-200">Log in</Link>}
        </div>
      )}
    </header>
  );
}
